import { useEffect } from 'react'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router'
import { AxiosError } from 'axios'
import {
  browserTracingIntegration,
  captureException,
  reactRouterBrowserTracingIntegration,
  init,
  withScope,
} from '@sentry/react'

const isProd = import.meta.env.VITE_ENVIRONMENT === 'prod'

function serializeUnknown(value: unknown) {
  try {
    if (typeof value === 'string') return value
    const serialized = JSON.stringify(value)
    return serialized ?? String(value)
  } catch {
    return String(value)
  }
}

function isUnhandledPromiseRejectionEvent(event: {
  exception?: {
    values?: Array<{
      type?: string
      value?: string
    }>
  }
}) {
  return (
    event.exception?.values?.some((v) => {
      const type = v.type?.toLowerCase() || ''
      const value = v.value?.toLowerCase() || ''
      return (
        type.includes('unhandledrejection') ||
        value.includes('captured as promise rejection')
      )
    }) ?? false
  )
}

if (isProd) {
  init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      browserTracingIntegration(),
      reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    environment: import.meta.env.VITE_ENVIRONMENT,

    // We recommend adjusting this value in production, or using tracesSampler
    // For finer control
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    beforeSend(event, hint) {
      const original = hint.originalException as
        | {
            reason?: unknown
            name?: string
            code?: string
            type?: string
            __pdCapturedByInterceptor?: boolean
            detail?: unknown
          }
        | undefined

      const originalCode =
        typeof original?.code === 'string' ? original.code : undefined

      const isFirebase =
        original?.name === 'FirebaseError' || originalCode?.includes('firebase')

      const isRemoteConfigFlake = [
        'remoteconfig/storage-open',
        'remoteconfig/storage-get',
        'remoteconfig/storage-set',
      ].includes(originalCode || '')

      // Ignore Firebase Remote Config flakes
      if (isFirebase && isRemoteConfigFlake) {
        return null
      }

      if (
        original?.__pdCapturedByInterceptor &&
        isUnhandledPromiseRejectionEvent(event)
      ) {
        return null
      }

      const reason = original?.reason !== undefined ? original.reason : original

      // Normalize non-Error unhandled promise rejection payloads (e.g. CustomEvent)
      if (reason && !(reason instanceof Error)) {
        const reasonType =
          typeof reason === 'object' && reason !== null
            ? reason.constructor?.name
            : typeof reason

        event.tags = {
          ...event.tags,
          rejection_reason_type: reasonType || 'unknown',
        }

        event.contexts = {
          ...event.contexts,
          unhandled_rejection: {
            reasonType,
            reasonValue: serializeUnknown(reason),
            eventType:
              original?.type !== undefined
                ? serializeUnknown(original.type)
                : undefined,
            eventDetail:
              original?.detail !== undefined
                ? serializeUnknown(original.detail)
                : undefined,
          },
        }

        if (!event.fingerprint) {
          event.fingerprint = [
            'unhandledrejection',
            reasonType || 'unknown',
            original?.type !== undefined
              ? serializeUnknown(original.type)
              : 'unknown',
          ]
        }
      }

      return event
    },
  })
}

/**
 * Enriches the current Sentry scope with details from an AxiosError, for improved error logging of HTTP errors.
 * This is used in the response interceptor of our Axios instances to ensure all HTTP errors are logged with relevant context.
 */
export function enrichScopeForHttpError(error: AxiosError) {
  const status = error.response?.status
  const url = error.config?.url
  const method = error.config?.method?.toUpperCase()

  // Only log in production
  if (!isProd) {
    return
  }

  // Only log client errors (4xx) and server errors (5xx)
  if (!status || status < 400) {
    return
  }

  // Don't log 401s - they're handled by auth flow
  if (status === 401) {
    return
  }

  const capturedError = error as AxiosError & {
    __pdCapturedByInterceptor?: boolean
  }
  capturedError.__pdCapturedByInterceptor = true

  withScope((scope) => {
    scope.setContext('http_request', {
      method,
      url,
      status,
    })

    captureException(capturedError)
  })
}
